/**
 * Nav sidebar badge counts keyed by nav leaf `id`.
 *
 * Policy: only include leaves with count > 0 and an implemented resolver.
 * Never emit 0 — users treat zero pills as broken alerts.
 *
 * Deferred (omit from map; do not default to 0):
 * - distributions.pending — Linear HB-NAV-001: owner unacknowledged distributions count
 * - banking.unmatched — Linear HB-NAV-002: reconciliation unmatched line count
 */
import { useMemo } from 'react';
import { UserRole } from '@repo/shared-types';
import { usePMMetrics } from './queries/usePMMetrics';
import { useOwnerMetrics } from './queries/useOwnerMetrics';
import { useCurrentOwnerId } from './queries/useEntityId';

export function useNavBadgeCounts(userRole: UserRole): Record<string, number> | undefined {
  const { data: pmMetrics } = usePMMetrics();
  const { data: ownerId } = useCurrentOwnerId();
  const { data: ownerMetrics } = useOwnerMetrics(
    userRole === UserRole.OWNER ? ownerId ?? undefined : undefined,
  );

  return useMemo(() => {
    const counts: Record<string, number> = {};

    if (userRole === UserRole.PROPERTY_MANAGER || userRole === UserRole.ADMIN) {
      const activeTickets = pmMetrics?.activeTickets ?? 0;
      if (activeTickets > 0) counts['maintenance.work_orders'] = activeTickets;

      const overdueCount = pmMetrics?.overdueReceivableCount ?? 0;
      if (overdueCount > 0) counts['accounting.payments'] = overdueCount;
    }

    if (userRole === UserRole.OWNER) {
      const pendingTickets = ownerMetrics?.pendingApprovalTicketCount ?? 0;
      if (pendingTickets > 0) counts['maintenance.work_orders'] = pendingTickets;
    }

    return Object.keys(counts).length > 0 ? counts : undefined;
  }, [userRole, pmMetrics?.activeTickets, pmMetrics?.overdueReceivableCount, ownerMetrics?.pendingApprovalTicketCount]);
}
